/**
 * xssProtection.js
 * XSS 방지를 위한 입력 데이터 정화 유틸리티 (관리자 프론트엔드)
 * SI-05 Phase 7: React 관리자 시스템 XSS 방지 구현
 */

import DOMPurify from 'dompurify';

/**
 * XSS 방지 설정
 */
const XSS_CONFIG = {
  // 기본 정화 설정 (모든 HTML 태그 제거)
  default: {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
    KEEP_CONTENT: true,
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false,
    RETURN_DOM_IMPORT: false
  },
  
  // 제한적 HTML 허용 (관리자 콘텐츠용)
  limitedHtml: {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'br', 'p', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
    ALLOWED_ATTR: ['class'],
    KEEP_CONTENT: true
  },
  
  // 완전 텍스트만 허용
  textOnly: {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
    KEEP_CONTENT: true,
    STRIP_TAGS: true
  },
  
  // 일반 텍스트 (HTML 태그 제거, 내용 유지)
  text: {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
    KEEP_CONTENT: true
  },
  
  // URL 정화
  url: {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
    KEEP_CONTENT: true
  },
  
  // 이메일 정화
  email: {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
    KEEP_CONTENT: true
  },
  
  // HTML 콘텐츠 (기본 HTML 태그 허용)
  html: {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'br', 'p', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'a', 'img'],
    ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'class', 'style'],
    KEEP_CONTENT: true
  },
  
  // 관리자 에디터용 (더 많은 태그 허용, 이미지 스타일 보전)
  adminEditor: {
    ALLOWED_TAGS: [
      'b', 'i', 'em', 'strong', 'br', 'p', 'div', 'span',
      'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'blockquote', 'pre', 'code', 'table', 'thead', 'tbody',
      'tr', 'th', 'td', 'a', 'img'
    ],
    ALLOWED_ATTR: [
      'class', 'id', 'style', 'href', 'target', 'src', 'alt',
      'width', 'height', 'title', 'data-filename'
    ],
    ALLOWED_ATTR_BY_TAG: {
      'img': ['src', 'alt', 'width', 'height', 'style', 'class', 'title', 'data-filename'],
      'div': ['class', 'id', 'style'],
      'span': ['class', 'id', 'style'],
      'p': ['class', 'id', 'style'],
      'a': ['href', 'target', 'class', 'id', 'style', 'title']
    },
    KEEP_CONTENT: true,
    ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i,
    // Summernote 에디터 스타일 보전을 위한 화이트리스트
    ALLOWED_STYLE_PROPS: [
      'width', 'height', 'float', 'margin', 'padding', 'text-align',
      'font-size', 'font-weight', 'color', 'background-color',
      'border', 'border-radius', 'display'
    ],
    ALLOWED_CLASSES: [
      'note-float-left', 'note-float-right', 'note-float-none',
      'note-align-left', 'note-align-center', 'note-align-right',
      'note-align-justify'
    ]
  }
};

/**
 * 문자열 XSS 정화
 * @param {string} input - 정화할 입력 문자열
 * @param {string} mode - 정화 모드 ('default', 'limitedHtml', 'textOnly', 'adminEditor')
 * @returns {string} 정화된 문자열
 */
export const sanitizeString = (input, mode = 'default') => {
  if (typeof input !== 'string') {
    return '';
  }
  
  if (!input.trim()) {
    return input;
  }
  
  // textOnly 모드에서는 더 엄격한 정화 적용
  if (mode === 'textOnly') {
    // SQL Injection 패턴 제거
    let sanitized = input
      .replace(/['"`;]/g, '') // 따옴표, 세미콜론 제거
      .replace(/--/g, '') // SQL 주석 제거
      .replace(/\/\*[\s\S]*?\*\//g, '') // 블록 주석 제거
      .replace(/\b(DROP|DELETE|INSERT|UPDATE|SELECT|UNION|ALTER|CREATE|EXEC|EXECUTE)\b/gi, '') // SQL 키워드 제거
      .replace(/[<>]/g, ''); // HTML 태그 문자 제거
    
    // 영문, 숫자, 언더스코어, 하이픈만 허용 (관리자 ID용)
    if (sanitized.match(/^[a-zA-Z0-9_-]*$/)) {
      return sanitized;
    } else {
      // 허용되지 않는 문자가 있으면 허용되는 문자만 추출
      return sanitized.replace(/[^a-zA-Z0-9_-]/g, '');
    }
  }
  
  const config = XSS_CONFIG[mode] || XSS_CONFIG.default;
  
  // adminEditor 모드에서 Summernote 스타일 보전을 위한 추가 처리
  if (mode === 'adminEditor') {
    // 간단한 접근법: 이미지 태그의 style 속성을 수동으로 보존
    let processedInput = input;
    
    // 이미지 태그에서 style 속성 추출 및 보존
    const imgMatches = input.match(/<img[^>]*>/gi);
    if (imgMatches) {
      imgMatches.forEach(imgTag => {
        const styleMatch = imgTag.match(/style\s*=\s*["']([^"']*)["']/i);
        const classMatch = imgTag.match(/class\s*=\s*["']([^"']*)["']/i);
        
        if (styleMatch || classMatch) {
          // 허용된 스타일 속성만 필터링
          let allowedStyles = '';
          if (styleMatch) {
            const allowedProps = config.ALLOWED_STYLE_PROPS || [];
            const styles = styleMatch[1].split(';')
              .map(style => style.trim())
              .filter(style => {
                if (!style) return false;
                const prop = style.split(':')[0]?.trim().toLowerCase();
                return allowedProps.includes(prop);
              })
              .filter(Boolean);
            
            allowedStyles = styles.join('; ');
          }
          
          // 허용된 클래스만 필터링
          let allowedClasses = '';
          if (classMatch) {
            const allowedClassList = config.ALLOWED_CLASSES || [];
            const classes = classMatch[1].split(' ')
              .map(cls => cls.trim())
              .filter(cls => cls && (
                allowedClassList.includes(cls) || 
                cls.startsWith('note-') ||
                cls.startsWith('img-')
              ))
              .filter(Boolean);
            
            allowedClasses = classes.join(' ');
          }
          
          // 임시로 style과 class를 data 속성으로 저장
          let tempTag = imgTag;
          if (allowedStyles) {
            tempTag = tempTag.replace(/style\s*=\s*["'][^"']*["']/i, `data-temp-style="${allowedStyles}"`);
          }
          if (allowedClasses) {
            tempTag = tempTag.replace(/class\s*=\s*["'][^"']*["']/i, `data-temp-class="${allowedClasses}"`);
          }
          
          processedInput = processedInput.replace(imgTag, tempTag);
        }
      });
    }
    
    // DOMPurify로 정화
    const adminConfig = {
      ...config,
      ADD_ATTR: ['data-temp-style', 'data-temp-class'],
      ALLOW_DATA_ATTR: true
    };
    
    let result = DOMPurify.sanitize(processedInput, adminConfig);
    
    // 임시 data 속성을 다시 style과 class로 복원
    result = result.replace(/data-temp-style="([^"]*)"/g, 'style="$1"');
    result = result.replace(/data-temp-class="([^"]*)"/g, 'class="$1"');
    
    return result;
  }
  
  try {
    return DOMPurify.sanitize(input, config);
  } catch (error) {
    console.warn('XSS sanitization failed:', error);
    // Fallback: 기본 문자 이스케이프
    return escapeHtml(input);
  }
};

/**
 * 객체의 모든 문자열 속성 XSS 정화
 * @param {Object} obj - 정화할 객체
 * @param {string} mode - 정화 모드
 * @param {Array<string>} excludeFields - 정화에서 제외할 필드명 배열
 * @returns {Object} 정화된 객체
 */
export const sanitizeObject = (obj, mode = 'default', excludeFields = []) => {
  if (!obj || typeof obj !== 'object') {
    return obj;
  }
  
  const sanitized = {};
  
  for (const [key, value] of Object.entries(obj)) {
    if (excludeFields.includes(key)) {
      sanitized[key] = value;
      continue;
    }
    
    if (typeof value === 'string') {
      sanitized[key] = sanitizeString(value, mode);
    } else if (Array.isArray(value)) {
      sanitized[key] = sanitizeArray(value, mode, excludeFields);
    } else if (value && typeof value === 'object') {
      sanitized[key] = sanitizeObject(value, mode, excludeFields);
    } else {
      sanitized[key] = value;
    }
  }
  
  return sanitized;
};

/**
 * 배열의 모든 요소 XSS 정화
 * @param {Array} arr - 정화할 배열
 * @param {string} mode - 정화 모드
 * @param {Array<string>} excludeFields - 정화에서 제외할 필드명 배열
 * @returns {Array} 정화된 배열
 */
export const sanitizeArray = (arr, mode = 'default', excludeFields = []) => {
  if (!Array.isArray(arr)) {
    return arr;
  }
  
  return arr.map(item => {
    if (typeof item === 'string') {
      return sanitizeString(item, mode);
    } else if (item && typeof item === 'object') {
      return sanitizeObject(item, mode, excludeFields);
    } else {
      return item;
    }
  });
};

/**
 * 폼 데이터 XSS 정화
 * @param {Object} formData - 폼 데이터 객체
 * @param {Object} fieldConfig - 필드별 정화 설정
 * @returns {Object} 정화된 폼 데이터
 */
export const sanitizeFormData = (formData, fieldConfig = {}) => {
  if (!formData || typeof formData !== 'object') {
    return formData;
  }
  
  const sanitized = {};
  
  for (const [fieldName, value] of Object.entries(formData)) {
    const config = fieldConfig[fieldName] || {};
    const mode = config.mode || 'default';
    const skip = config.skip || false;
    
    if (skip) {
      sanitized[fieldName] = value;
      continue;
    }
    
    if (typeof value === 'string') {
      sanitized[fieldName] = sanitizeString(value, mode);
    } else if (Array.isArray(value)) {
      sanitized[fieldName] = sanitizeArray(value, mode);
    } else if (value && typeof value === 'object') {
      sanitized[fieldName] = sanitizeObject(value, mode);
    } else {
      sanitized[fieldName] = value;
    }
  }
  
  return sanitized;
};

/**
 * HTML 문자 이스케이프 (Fallback 함수)
 * @param {string} text - 이스케이프할 텍스트
 * @returns {string} 이스케이프된 텍스트
 */
export const escapeHtml = (text) => {
  if (typeof text !== 'string') {
    return text;
  }
  
  const htmlEscapes = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#x27;',
    '/': '&#x2F;'
  };
  
  return text.replace(/[&<>"'/]/g, (match) => htmlEscapes[match]);
};

/**
 * URL 안전성 검증
 * @param {string} url - 검증할 URL
 * @returns {boolean} URL 안전성 여부
 */
export const isUrlSafe = (url) => {
  if (typeof url !== 'string') {
    return false;
  }
  
  // 기본 URL 형식 검증
  try {
    const urlObj = new URL(url);
    
    // 허용된 프로토콜만 허용
    const allowedProtocols = ['http:', 'https:', 'mailto:', 'tel:'];
    if (!allowedProtocols.includes(urlObj.protocol)) {
      return false;
    }
    
    // JavaScript 프로토콜 차단
    if (urlObj.protocol === 'javascript:') {
      return false;
    }
    
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * 관리자 시스템 특화 정화 함수
 */
export const adminDataSanitizer = {
  /**
   * 공지사항 데이터 정화
   * @param {Object} noticeData - 공지사항 데이터
   * @returns {Object} 정화된 공지사항 데이터
   */
  sanitizeNoticeData: (noticeData) => {
    return sanitizeFormData(noticeData, {
      title: { mode: 'textOnly' },
      content: { mode: 'adminEditor' }, // 관리자 에디터 모드 허용
      isImportant: { skip: true }, // 불린 값은 정화하지 않음
      isActive: { skip: true }
    });
  },
  
  /**
   * FAQ 데이터 정화
   * @param {Object} faqData - FAQ 데이터
   * @returns {Object} 정화된 FAQ 데이터
   */
  sanitizeFaqData: (faqData) => {
    return sanitizeFormData(faqData, {
      question: { mode: 'textOnly' },
      answer: { mode: 'adminEditor' },
      category: { mode: 'textOnly' },
      isActive: { skip: true }
    });
  },
  
  /**
   * 사용자 데이터 정화
   * @param {Object} userData - 사용자 데이터
   * @returns {Object} 정화된 사용자 데이터
   */
  sanitizeUserData: (userData) => {
    return sanitizeFormData(userData, {
      name: { mode: 'textOnly' },
      email: { mode: 'textOnly' },
      phoneNumber: { mode: 'textOnly' },
      birthDate: { skip: true }, // 날짜는 정화하지 않음
      gender: { mode: 'textOnly' }
    });
  },
  
  /**
   * 시스템 설정 데이터 정화
   * @param {Object} settingsData - 시스템 설정 데이터
   * @returns {Object} 정화된 시스템 설정 데이터
   */
  sanitizeSystemSettings: (settingsData) => {
    return sanitizeFormData(settingsData, {
      title: { mode: 'textOnly' },
      message: { mode: 'limitedHtml' },
      alertType: { mode: 'textOnly' },
      targetUsers: { mode: 'textOnly' },
      scheduledTime: { skip: true } // 날짜는 정화하지 않음
    });
  }
};

/**
 * API 요청 데이터 정화 미들웨어
 * @param {Object} requestData - API 요청 데이터
 * @param {string} endpoint - API 엔드포인트
 * @returns {Object} 정화된 요청 데이터
 */
export const sanitizeApiRequest = (requestData, endpoint) => {
  if (!requestData) {
    return requestData;
  }
  
  // 엔드포인트별 특화 정화
  switch (true) {
    case endpoint.includes('/auth/'):
      return sanitizeFormData(requestData, {
        adminId: { mode: 'textOnly' },
        password: { skip: true }, // 비밀번호는 해시되므로 정화하지 않음
        currentPassword: { skip: true },
        newPassword: { skip: true },
        confirmPassword: { skip: true }
      });
      
    case endpoint.includes('/notice/'):
      return adminDataSanitizer.sanitizeNoticeData(requestData);
      
    case endpoint.includes('/faq/'):
      return adminDataSanitizer.sanitizeFaqData(requestData);
      
    case endpoint.includes('/user/'):
      return adminDataSanitizer.sanitizeUserData(requestData);
      
    case endpoint.includes('/system/'):
      return adminDataSanitizer.sanitizeSystemSettings(requestData);
      
    default:
      return sanitizeObject(requestData, 'default');
  }
};

/**
 * XSS 방지 검증 결과
 * @param {string} original - 원본 문자열
 * @param {string} sanitized - 정화된 문자열
 * @returns {Object} 검증 결과
 */
export const validateXssPrevention = (original, sanitized) => {
  const hasXssAttempt = original !== sanitized;
  const suspiciousPatterns = [
    /<script/i,
    /javascript:/i,
    /on\w+\s*=/i,
    /<iframe/i,
    /<object/i,
    /<embed/i,
    /eval\s*\(/i,
    /expression\s*\(/i,
    /<svg/i,
    /<math/i,
    /data:/i,
    /vbscript:/i
  ];
  
  const hasSuspiciousContent = suspiciousPatterns.some(pattern => 
    pattern.test(original)
  );
  
  return {
    isClean: !hasXssAttempt,
    hasSuspiciousContent,
    sanitized,
    riskLevel: hasSuspiciousContent ? 'HIGH' : (hasXssAttempt ? 'MEDIUM' : 'LOW'),
    detectedPatterns: suspiciousPatterns.filter(pattern => pattern.test(original))
  };
};

/**
 * 관리자 권한 검증을 위한 보안 토큰 정화
 * @param {string} token - 검증할 토큰
 * @returns {string} 정화된 토큰
 */
export const sanitizeSecurityToken = (token) => {
  if (typeof token !== 'string') {
    return '';
  }
  
  // JWT 토큰 형식 검증 (헤더.페이로드.서명)
  const jwtPattern = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;
  
  if (!jwtPattern.test(token)) {
    console.warn('Invalid token format detected');
    return '';
  }
  
  return token;
};

/**
 * 파일 업로드 보안 검증
 * @param {File} file - 업로드할 파일
 * @returns {Object} 검증 결과
 */
export const validateFileUpload = (file) => {
  if (!file) {
    return {
      isValid: false,
      message: '파일을 선택해주세요'
    };
  }
  
  // 허용된 MIME 타입
  const allowedImageTypes = ['image/jpeg', 'image/png', 'image/gif'];
  const allowedDocumentTypes = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ];
  const allowedTypes = [...allowedImageTypes, ...allowedDocumentTypes];
  
  // MIME 타입 검증
  if (!allowedTypes.includes(file.type)) {
    return {
      isValid: false,
      message: '허용되지 않는 파일 형식입니다'
    };
  }
  
  // 파일 크기 검증 (10MB)
  const maxSize = 10 * 1024 * 1024;
  if (file.size > maxSize) {
    return {
      isValid: false,
      message: '파일 크기는 10MB 이하여야 합니다'
    };
  }
  
  // 파일명 보안 검증
  const dangerousExtensions = ['.exe', '.bat', '.cmd', '.scr', '.pif', '.js', '.vbs'];
  const fileName = file.name.toLowerCase();
  
  if (dangerousExtensions.some(ext => fileName.endsWith(ext))) {
    return {
      isValid: false,
      message: '위험한 파일 확장자입니다'
    };
  }
  
  return {
    isValid: true,
    message: null
  };
};

/**
 * 일반적인 입력값 정화 함수
 * @param {string} input - 정화할 입력값
 * @returns {string} 정화된 입력값
 */
export const sanitizeInput = (input) => {
    if (typeof input !== 'string') {
        return String(input || '');
    }
    
    // URL 디코딩 후 처리 (인코딩된 태그 방지)
    let decoded = input;
    try {
        decoded = decodeURIComponent(input);
    } catch (e) {
        // 디코딩 실패 시 원본 사용
        decoded = input;
    }
    
    // 기본적인 XSS 패턴 제거
    return decoded
        .replace(/<[^>]*>/g, '') // 모든 HTML 태그 제거
        .replace(/javascript:/gi, '')
        .replace(/on\w+\s*=/gi, '')
        .replace(/data:text\/html/gi, '')
        .replace(/[<>'"]/g, '') // 위험한 문자 제거
        .trim();
};

/**
 * 숫자 입력값 검증 함수
 * @param {string|number} input - 검증할 입력값
 * @param {Object} options - 검증 옵션
 * @param {number} options.min - 최소값
 * @param {number} options.max - 최대값
 * @param {boolean} options.allowEmpty - 빈 값 허용 여부
 * @returns {Object} 검증 결과 {isValid: boolean, error: string|null}
 */
export const validateNumericInput = (input, options = {}) => {
    const { min = 0, max = Number.MAX_SAFE_INTEGER, allowEmpty = true } = options;
    
    // 빈 값 처리
    if (!input || input === '') {
        return allowEmpty 
            ? { isValid: true, error: null }
            : { isValid: false, error: '값을 입력해주세요.' };
    }
    
    // 숫자가 아닌 문자 포함 검사
    const numericString = String(input).replace(/[^0-9.-]/g, '');
    if (numericString !== String(input)) {
        return { isValid: false, error: '숫자만 입력 가능합니다.' };
    }
    
    const numericValue = Number(input);
    
    // NaN 검사
    if (isNaN(numericValue)) {
        return { isValid: false, error: '올바른 숫자를 입력해주세요.' };
    }
    
    // 범위 검사
    if (numericValue < min) {
        return { isValid: false, error: `최소값은 ${min}입니다.` };
    }
    
    if (numericValue > max) {
        return { isValid: false, error: `최대값은 ${max}입니다.` };
    }
    
    return { isValid: true, error: null };
};
